import { ThisConnector } from '../../../../api/connector'

export interface GetAvailableAgentsArgs {
  organizationUuid: string
}

interface ApiAgentLanguagesResponse {
  languages: Array<
    | 'Java'
    | '.NET'
    | '.NET Core'
    | 'Node'
    | 'Ruby'
    | 'Proxy'
    | 'Python'
    | 'PHP'
    | 'Go'
  >
}

type ApiAgentType =
  | 'JAVA'
  | 'DOTNET'
  | 'DOTNET_CORE'
  | 'DOTNET_CORE_INSTALLER_FOR_IIS'

interface ApiAgentVersionsResponse {
  availableHostInstallerAgents: any[]
  agents: Array<{
    name: ApiAgentType
    value: string
  }>
}

export async function getAvailableAgents(
  this: ThisConnector,
  { organizationUuid }: GetAvailableAgentsArgs
) {
  // Get the list of enabled agent languages
  // The string values are the "human readable" (or "display") variations, such as ".NET", ".NET Core", and "Java".
  const { languages: enabledAgentLanguages } =
    await this.connector.get<ApiAgentLanguagesResponse>(
      `${organizationUuid}/languages`
    )

  const enabledAgentTypes = mapEnabledAgentTypes(enabledAgentLanguages)

  // Get available agents and host installer agents
  const {
    // This is an object with `name` of the agent and `value` for the agent version
    agents
  } = await this.connector.get<ApiAgentVersionsResponse>(
    `${organizationUuid}/agents/versions`
  )

  return {
    enabledAgentTypes,
    availability: buildAgentAvailabilityList(enabledAgentTypes, agents)
  }
}

export type AvailableAgentLanguage = 'Java' | '.NET' | '.NET Core' | 'Proxy'

export function mapEnabledAgentTypes(
  enabledAgentTypes: Array<AvailableAgentLanguage | string>
) {
  return enabledAgentTypes.map((lang) => {
    // Map standard "display" agent names to AgentType
    const type = agentLanguageMap[lang]
    if (type) {
      return type as AgentType
    }

    // Convert values like this:
    // PROXY-contrast-webserver-agent-nginx-1.12.1~trusty.amd64.deb
    // to this:
    // PROXY_CONTRAST_WEBSERVER_AGENT_NGINX_1.12.1~trusty.amd64.deb
    return lang.toUpperCase().replace('-', '_')
  })
}

const agentLanguageMap: Record<AvailableAgentLanguage, AgentType> = {
  Java: 'JAVA',
  '.NET': 'DOTNET',
  '.NET Core': 'DOTNET_CORE',
  Proxy: 'PROXY'
}

export type AgentType =
  | 'JAVA'
  | 'JAVA_HOST'
  | 'DOTNET'
  | 'DOTNET_CORE'
  | 'CONTRAST_SERVICE'
  | 'PROXY'
  | 'GO'

export interface AgentAvailability {
  type: AgentType
  name?: string
  version?: string
  enabled: boolean
}

interface ApiAvailableAgent {
  name: string
  value: string
}

function buildAgentAvailabilityList(
  enabledAgentTypes: string[],
  availableAgents: ApiAvailableAgent[]
) {
  return availableAgents.map(({ name: rawName, value: version }) => {
    if (rawName.indexOf('-') > -1) {
      return parseNameValuePair(enabledAgentTypes, rawName, version)
    }
    const type = rawName as AgentType
    return {
      type,
      version,
      enabled: enabledAgentTypes.some((agentType) => agentType === type)
    }
  })
}

export function parseNameValuePair(
  enabledAgentTypes: string[],
  rawName: string,
  version?: string
) {
  const index = rawName.indexOf('-')
  const type = rawName.slice(0, index) as AgentType
  const name = rawName.slice(index + 1)
  return {
    type,
    name,
    version,
    enabled: enabledAgentTypes.some((agentType) => agentType === type)
  }
}
